import React, { useState } from 'react';
import { DatePicker, Form, Input, Radio, Select, Space } from 'antd';
import moment from 'moment';
import ActionBar from '../../../components/common/ActionBar';
import ContactHelpText from '../../../components/common/ContactHelpText';
import DetailRow from '../../../components/common/DetailRow';
import Note from '../../../components/common/Note';
import {
  Card,
  CardBody,
  PageHead,
  PageSubtitle,
  PageTitle,
  SectionTitle,
} from '../../../components/common/Card';
import {
  BookingForm,
  FieldRow,
  FormCardBody,
} from '../../../components/NewBooking/BookingForm';
import { CARD_TYPE, CARD_TYPE_LABEL } from '../../../constants/cardType';
import {
  DVA_CARD_TYPE,
  DVA_CARD_TYPE_OPTIONS,
} from '../../../constants/dvaCardType';
import { TIME_FORMAT } from '../../../constants/time';
import {
  DVA_NUMBER_PATTERN,
  MEDICARE_IRN_PATTERN,
  getCoverChoices,
  isExpiryCurrent,
  isUnchangedMedicareOnFile,
  isValidMedicareNumber,
} from '../../../utils/rescheduleUtils';
import { scrollToFirstFormError } from '../../../utils/scrollUtils';

const { Option } = Select;

const COVER_SECTION_TITLE = {
  [CARD_TYPE.MEDICARE]: 'Medicare card',
  [CARD_TYPE.DVA]: 'DVA card',
};

const MEDICARE_IRN_HINT = 'The number beside your name on the card.';
const NO_CARD_ON_FILE_MESSAGE =
  "We don't have a Medicare or DVA card on file for you, so the private fee applies. If you have one, you can add it now. This is optional.";

const toFormCover = (cover) => ({
  cardType: cover?.cardType || CARD_TYPE.NONE,
  medicareCardNumber: cover?.medicareCardNumber,
  medicareCardIRN: cover?.medicareCardIRN,
  expiry:
    cover?.expiry ||
    (cover?.expiredDate ? moment(cover.expiredDate, 'YYYY-MM-DD') : undefined),
  dvaNumber: cover?.dvaNumber,
  dvaCardType: cover?.dvaCardType,
});

const ConfirmDetails = ({
  workflow,
  contact,
  coverOnFile,
  coverDraft,
  onBack,
  onNext,
}) => {
  const [form] = Form.useForm();
  const [cardType, setCardType] = useState(
    () => toFormCover(coverDraft || coverOnFile).cardType
  );
  const coverChoices = getCoverChoices(coverOnFile);
  // A card on file may be any DVA colour (Lilac and Blue included); a new one is limited to
  // the colours booking offers.
  const dvaCardTypeOptions =
    coverOnFile?.cardType === CARD_TYPE.DVA
      ? DVA_CARD_TYPE
      : DVA_CARD_TYPE_OPTIONS;
  const hasCardOnFile = coverChoices.length === 1;
  const coverSectionTitle = hasCardOnFile
    ? COVER_SECTION_TITLE[coverChoices[0]]
    : 'Medicare or DVA card';
  const { client } = workflow || {};
  const patientName = [client?.firstName, client?.lastName]
    .filter(Boolean)
    .join(' ');

  const handleNext = async () => {
    try {
      const { cover } = await form.validateFields();
      onNext(cover);
    } catch (validationError) {
      scrollToFirstFormError(form, validationError);
    }
  };

  return (
    <>
      <PageHead>
        <PageTitle>Your details</PageTitle>
        <PageSubtitle>
          Check these are still correct before you confirm the new time.
        </PageSubtitle>
      </PageHead>

      <Card>
        <CardBody>
          <SectionTitle>Personal details</SectionTitle>
          {patientName && <DetailRow label="Name" value={patientName} />}
          {client?.phoneNumberMobile && (
            <DetailRow label="Mobile" value={client.phoneNumberMobile} />
          )}
          {client?.email && <DetailRow label="Email" value={client.email} />}
          <Note>
            {`Your new confirmation is sent to this mobile and email. If any of them are wrong, please call us on ${contact.phone}.`}
          </Note>
        </CardBody>
      </Card>

      <BookingForm
        layout="vertical"
        form={form}
        initialValues={{ cover: toFormCover(coverDraft || coverOnFile) }}
      >
        <Card>
          <FormCardBody>
            <SectionTitle>{coverSectionTitle}</SectionTitle>

            {hasCardOnFile ? (
              // The card on the record, editable; it can't be swapped or dropped here.
              <Form.Item name={['cover', 'cardType']} hidden>
                <Input />
              </Form.Item>
            ) : (
              <>
                <Note tone="warning">{NO_CARD_ON_FILE_MESSAGE}</Note>
                <Form.Item name={['cover', 'cardType']}>
                  <Radio.Group onChange={(e) => setCardType(e.target.value)}>
                    <Space direction="vertical">
                      {coverChoices.map((choice) => (
                        <Radio value={choice} key={choice}>
                          {CARD_TYPE_LABEL[choice]}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </>
            )}

            {cardType === CARD_TYPE.MEDICARE && (
              <>
                <Form.Item
                  label="Medicare Card Number"
                  required
                  name={['cover', 'medicareCardNumber']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input the Medicare card number.',
                    },
                    {
                      // The card already on file is left alone; a new or changed one
                      // must pass the check digit, as patient-api requires.
                      validator: (_rule, value) =>
                        !value ||
                        value === coverOnFile?.medicareCardNumber ||
                        isValidMedicareNumber(value)
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(
                                'Please input a valid Medicare card number.'
                              )
                            ),
                    },
                  ]}
                >
                  <Input placeholder="*********" inputMode="numeric" />
                </Form.Item>

                <FieldRow>
                  <Form.Item
                    label="Medicare Card IRN"
                    required
                    extra={MEDICARE_IRN_HINT}
                    name={['cover', 'medicareCardIRN']}
                    rules={[
                      {
                        required: true,
                        message: 'Please input your Medicare card IRN.',
                      },
                      {
                        pattern: MEDICARE_IRN_PATTERN,
                        message: 'Please input a valid Medicare card IRN.',
                      },
                    ]}
                  >
                    <Input placeholder="*" inputMode="numeric" />
                  </Form.Item>

                  <Form.Item
                    label="Expiry"
                    required
                    name={['cover', 'expiry']}
                    dependencies={[
                      ['cover', 'medicareCardNumber'],
                      ['cover', 'medicareCardIRN'],
                    ]}
                    rules={[
                      {
                        required: true,
                        message: 'Please select the expiry date.',
                      },
                      {
                        // An expired card is refused by patient-api. The card on file,
                        // untouched, is left alone since it isn't sent; once any field
                        // changes the whole card is sent, so the expiry must be current.
                        validator: (_rule, value) =>
                          !value ||
                          isUnchangedMedicareOnFile(
                            {
                              ...form.getFieldValue('cover'),
                              expiry: value,
                            },
                            coverOnFile
                          ) ||
                          isExpiryCurrent(value)
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error('This card has expired.')
                              ),
                      },
                    ]}
                  >
                    <DatePicker
                      picker="month"
                      placeholder="MM/YYYY"
                      format={TIME_FORMAT.TABLE_MONTH}
                    />
                  </Form.Item>
                </FieldRow>
              </>
            )}

            {cardType === CARD_TYPE.DVA && (
              <FieldRow>
                <Form.Item
                  label="DVA Card Number"
                  required
                  name={['cover', 'dvaNumber']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input your DVA card number.',
                    },
                    {
                      pattern: DVA_NUMBER_PATTERN,
                      message: 'Please input a valid DVA card number.',
                    },
                  ]}
                  normalize={(value) => (value ? value.toUpperCase() : value)}
                >
                  <Input placeholder="Enter DVA card number" />
                </Form.Item>

                <Form.Item
                  label="DVA Card type"
                  required
                  name={['cover', 'dvaCardType']}
                  rules={[
                    {
                      required: true,
                      message: 'Please select your DVA card type.',
                    },
                  ]}
                >
                  <Select placeholder="Please select DVA card type.">
                    {dvaCardTypeOptions.map(({ value, label }) => (
                      <Option value={value} key={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </FieldRow>
            )}
          </FormCardBody>
        </Card>
      </BookingForm>

      <ContactHelpText phone={contact.phone} />

      <ActionBar
        backLabel="Back"
        onBack={onBack}
        nextLabel="Next"
        onNext={handleNext}
      />
    </>
  );
};

export default ConfirmDetails;
